/** @file puppetLoading: open a puppet (load, live socket, first preview) and close it (dispose everything). */
import type { PuppetDoc } from '../state/puppetDoc';
import type { EditorActions, EditorState } from '../state/editorTypes';
import { initialEditorState } from '../state/initialEditorState';
import { applyLocalBatch } from '../state/testing/applyLocalBatch';
import { asPose3DError } from './asPose3DError';
import type { ApplyFn } from './commandQueue';
import { commandQueue } from './commandQueue';
import { errorText } from './errorText';
import type { LiveCommit } from './liveSync';
import { liveSync } from './liveSync';
import type { ServerContext } from './serverContext';
import { toStoredPuppet } from './toStoredPuppet';

/** What opening needs from the other action files: the live commit handler and the immediate refresh. */
export type LoadingDeps = { onCommit: (commit: LiveCommit) => void; refreshNow: () => Promise<void> };

/** A batch that fails locally leaves the document as it was; the server's answer and the resync decide. */
const localApply = (now: () => number): ApplyFn => (doc, commands) => {
  const result = applyLocalBatch(doc, commands, doc.images, now());
  return result.replies.every((r) => r.ok) ? result.doc : doc;
};

/** The puppet-independent slice every open and close starts from. */
const cleared = (url: string): Pick<EditorState, 'puppet' | 'preview' | 'selection' | 'poseOverride'> => {
  const base = initialEditorState(url);
  return { puppet: base.puppet, preview: base.preview, selection: base.selection, poseOverride: null };
};

/** The playhead's clip if the document still has it, else its first clip (or none). */
const clipIn = (doc: PuppetDoc, clip: string | null): string | null =>
  clip !== null && clip in doc.clips ? clip : (Object.keys(doc.clips)[0] ?? null);

/** `openPuppet` closes the current puppet first; answers for a puppet closed meanwhile are dropped. */
export function puppetLoading(ctx: ServerContext, deps: LoadingDeps): Pick<EditorActions, 'openPuppet' | 'closePuppet'> {
  const closePuppet = (): void => {
    ctx.token += 1;
    ctx.live?.close();
    ctx.live = null;
    ctx.queue = null;
    ctx.dirty = false;
    ctx.schedule.cancel();
    ctx.set(cleared(ctx.api.url));
  };
  const connect = (id: string, revision: number, token: number): void => {
    ctx.live = liveSync(ctx.api, id, ctx.actor, revision, {
      onState: (live) => { if (token === ctx.token) ctx.set((s) => ({ puppet: { ...s.puppet, live } })); },
      onCommit: (commit) => { if (token === ctx.token) deps.onCommit(commit); },
      onError: (e) => { if (token === ctx.token) ctx.set((s) => ({ puppet: { ...s.puppet, lastError: errorText(e) } })); },
    });
  };
  const openPuppet = async (id: string): Promise<void> => {
    closePuppet();
    const token = ctx.token;
    ctx.set((s) => ({ puppet: { ...s.puppet, id } }));
    try {
      const wire = await ctx.api.puppets.get(id);
      const doc = toStoredPuppet(wire);
      if (token !== ctx.token) return;
      ctx.queue = commandQueue(doc, localApply(ctx.now));
      ctx.set((s) => ({
        puppet: { ...s.puppet, projectId: wire.project, doc, revision: doc.revision },
        playhead: { ...s.playhead, clip: clipIn(doc, s.playhead.clip), frame: 0, playing: false },
      }));
      connect(id, doc.revision, token);
      void ctx.api.puppets.revisions(id, { from: doc.revision, limit: 1 }).then((r) => {
        if (token === ctx.token) ctx.set((s) => ({ puppet: { ...s.puppet, history: r.history ?? null } }));
      }, () => undefined);
      await deps.refreshNow();
    } catch (e) {
      if (token !== ctx.token) return;
      const text = errorText(asPose3DError(e));
      ctx.set((s) => ({ puppet: { ...s.puppet, lastError: text } }));
      ctx.get().notify('error', `Could not open ${id}: ${text}`);
    }
  };
  return { openPuppet, closePuppet };
}
